import i18next from 'i18next';
import * as Yup from 'yup';
import { formatMoneyNumeral } from '../util/helpers/numeralHelpers';

const MAX_DELIVERY_AMOUNT = 225000;

export const buyForDeliveryValidationSchema = ({
  activeType,
  unitPrice,
  balanceAvailableForTrading,
  getMinimumOrderAmount,
  locationName,
  settings,
}) => {
  const minimumOrderByLocation = getMinimumOrderAmount();

  const calculateMaxQuantity = () =>
    Math.floor(balanceAvailableForTrading / unitPrice);

  const calculateMinimumQuantity = () => {
    if (!minimumOrderByLocation) {
      return 1;
    }

    return Math.ceil(
      (unitPrice > minimumOrderByLocation
        ? unitPrice
        : minimumOrderByLocation) / unitPrice,
    );
  };

  if (activeType === 'dollars') {
    if (minimumOrderByLocation) {
      return Yup.object().shape({
        CashAmount: Yup.number()
          .test(
            'max-two-decimals',
            'Amount can only include up to two decimal places',
            (value) => {
              if (value === undefined || value === null) return true;
              return /^\d+(\.\d{1,2})?$/.test(value.toString());
            },
          )
          // eslint-disable-next-line func-names
          .test('max-delivery-cash', '', function (value) {
            if (!value) return true;

            if (value > MAX_DELIVERY_AMOUNT) {
              return this.createError({
                message: i18next.t(
                  'validationSchema.orders.maximumDeliveryOrder',
                  {
                    amount: formatMoneyNumeral(MAX_DELIVERY_AMOUNT),
                    phoneTollFree: settings?.SupportPhoneTollFree,
                    phoneInternational: settings?.SupportPhoneInternational,
                  },
                ),
              });
            }

            return true;
          })
          // eslint-disable-next-line func-names
          .test('min-cash-with-difference', '', function (value) {
            if (!value || !minimumOrderByLocation) return true;

            const requiredMin = Math.max(unitPrice, minimumOrderByLocation);

            if (value < requiredMin) {
              const missingAmount = requiredMin - value;

              return this.createError({
                message: i18next.t(
                  'validationSchema.orders.minimumCashFundsForLocationDelivery',
                  {
                    amount: formatMoneyNumeral(missingAmount),
                    minimum: formatMoneyNumeral(requiredMin),
                    location: locationName,
                  },
                ),
              });
            }

            return true;
          })
          .when('applyMax', {
            is: true,
            then: Yup.number().max(
              balanceAvailableForTrading,
              i18next.t('validationSchema.orders.insufficientFunds', {
                balance: `${formatMoneyNumeral(balanceAvailableForTrading)}`,
              }),
            ),
          })
          .typeError(i18next.t('validationSchema.cash.cashRequired'))
          .required(i18next.t('validationSchema.cash.cashRequired')),
      });
    }

    if (!minimumOrderByLocation) {
      return Yup.object().shape({
        CashAmount: Yup.number()
          .test(
            'max-two-decimals',
            'Amount can only include up to two decimal places',
            (value) => {
              if (value === undefined || value === null) return true;
              return /^\d+(\.\d{1,2})?$/.test(value.toString());
            },
          )
          // eslint-disable-next-line func-names
          .test('max-delivery-cash', '', function (value) {
            if (!value) return true;

            if (value > MAX_DELIVERY_AMOUNT) {
              return this.createError({
                message: i18next.t(
                  'validationSchema.orders.maximumDeliveryOrder',
                  {
                    amount: formatMoneyNumeral(MAX_DELIVERY_AMOUNT),
                    phoneTollFree: settings?.SupportPhoneTollFree,
                    phoneInternational: settings?.SupportPhoneInternational,
                  },
                ),
              });
            }

            return true;
          })
          .min(
            unitPrice,
            i18next.t('validationSchema.orders.minimumFunds', {
              productPrice: `${formatMoneyNumeral(unitPrice)}`,
            }),
          )
          .when('applyMax', {
            is: true,
            then: Yup.number().max(
              balanceAvailableForTrading,
              i18next.t('validationSchema.orders.insufficientFunds', {
                balance: `${formatMoneyNumeral(balanceAvailableForTrading)}`,
              }),
            ),
          })
          .typeError(i18next.t('validationSchema.cash.cashRequired'))
          .required(i18next.t('validationSchema.cash.cashRequired')),
      });
    }
  }

  if (activeType === 'quantity') {
    if (minimumOrderByLocation) {
      return Yup.object().shape({
        QuantityAmount: Yup.number()
          .integer(i18next.t('validationSchema.quantity.wholeNumber'))
          // eslint-disable-next-line func-names
          .test('max-delivery-quantity', '', function (value) {
            if (!value) return true;

            const totalValue = value * unitPrice;

            if (totalValue > MAX_DELIVERY_AMOUNT) {
              return this.createError({
                message: i18next.t(
                  'validationSchema.orders.maximumDeliveryOrder',
                  {
                    amount: formatMoneyNumeral(MAX_DELIVERY_AMOUNT),
                    phoneTollFree: settings?.SupportPhoneTollFree,
                    phoneInternational: settings?.SupportPhoneInternational,
                  },
                ),
              });
            }

            return true;
          })
          // eslint-disable-next-line func-names
          .test('min-quantity-with-difference', '', function (value) {
            if (!value || !minimumOrderByLocation) return true;

            const minQty = calculateMinimumQuantity();

            if (value < minQty) {
              const missingQty = minQty - value;

              return this.createError({
                message: i18next.t(
                  'validationSchema.orders.minimumQuantityFundsForLocationDelivery',
                  {
                    amount: missingQty,
                    minimum: minQty,
                    productPrice: formatMoneyNumeral(
                      unitPrice > minimumOrderByLocation
                        ? unitPrice
                        : minimumOrderByLocation,
                    ),
                    location: locationName,
                  },
                ),
              });
            }

            return true;
          })
          .when('applyMax', {
            is: true,
            then: Yup.number().max(
              calculateMaxQuantity(),
              i18next.t('validationSchema.orders.insufficientFunds', {
                balance: `${formatMoneyNumeral(balanceAvailableForTrading)}`,
              }),
            ),
          })
          .typeError(i18next.t('validationSchema.quantity.quantityRequired'))
          .required(i18next.t('validationSchema.quantity.quantityRequired')),
      });
    }

    if (!minimumOrderByLocation) {
      return Yup.object().shape({
        QuantityAmount: Yup.number()
          .integer(i18next.t('validationSchema.quantity.wholeNumber'))
          // eslint-disable-next-line func-names
          .test('max-delivery-quantity', '', function (value) {
            if (!value) return true;

            const totalValue = value * unitPrice;

            if (totalValue > MAX_DELIVERY_AMOUNT) {
              return this.createError({
                message: i18next.t(
                  'validationSchema.orders.maximumDeliveryOrder',
                  {
                    amount: formatMoneyNumeral(MAX_DELIVERY_AMOUNT),
                    phoneTollFree: settings?.SupportPhoneTollFree,
                    phoneInternational: settings?.SupportPhoneInternational,
                  },
                ),
              });
            }

            return true;
          })
          .min(
            1,
            i18next.t('validationSchema.orders.minimumFunds', {
              productPrice: 1,
            }),
          )
          .when('applyMax', {
            is: true,
            then: Yup.number().max(
              calculateMaxQuantity(),
              i18next.t('validationSchema.orders.insufficientFunds', {
                balance: `${formatMoneyNumeral(balanceAvailableForTrading)}`,
              }),
            ),
          })
          .typeError(i18next.t('validationSchema.quantity.quantityRequired'))
          .required(i18next.t('validationSchema.quantity.quantityRequired')),
      });
    }
  }
};
